/**
 * Every question on /faqs, grouped. The homepage questions (HOME_FAQS) are
 * reused by reference so the two pages can never disagree.
 *
 * Rules (docs/content/WRITING_RULES.md): plain words, no dashes or hyphens in
 * visible text, 2 to 4 sentence answers, no disease or cure claims, and only
 * facts the company can prove. Answers are plain text because they also go
 * into FAQPage JSON-LD. An optional `link` renders under the answer.
 */
import { HOME_FAQS } from "@/content/faqs";
import { BRAND, LAB_REPORT } from "@/content/brand";

// Look up a homepage question by its exact wording, so reordering HOME_FAQS
// never silently swaps answers here.
function home(q) {
  const f = HOME_FAQS.find((x) => x.q === q);
  if (!f) throw new Error(`HOME_FAQS is missing: ${q}`);
  return f;
}

const sat = LAB_REPORT.rows.find((r) => r.param === "Saturated fat")?.value;

export const FAQ_GROUPS = [
  {
    id: "about-the-oil",
    title: "About the oil",
    items: [
      home("What does cold pressed coconut oil mean?"),
      {
        q: "What is in a jar of Karijeeva?",
        a: "One ingredient: coconut oil. It is not refined, bleached or deodorised, and we never blend in other oils or add colour or aroma.",
      },
      {
        q: "Where do your coconuts come from?",
        a: "From our family farms in Karnataka. Most grow in Tiptur, in Tumakuru district, and the rest on the Uttara Kannada coast between Gokarna and Shirali near Bhatkal.",
        link: { to: "/the-farm", label: "Visit our farms" },
      },
      {
        q: "Is Karijeeva virgin coconut oil?",
        a: "We sell it as cold pressed coconut oil. FSSAI has a separate standard for virgin coconut oil, and we only use a name we can stand behind. What we can tell you for certain is that it is pressed without added heat and never refined.",
      },
      {
        q: "Why does it smell and taste of coconut?",
        a: "Because nothing has been taken out. Refined oil is deodorised so it smells of nothing, while cold pressed oil keeps its natural coconut aroma and taste. That aroma is what dishes like thoran, avial and coconut chutney are built around.",
      },
      home("Why has my oil turned white and solid?"),
      home("Why does Karijeeva come in a jar and not a bottle?"),
      {
        q: "How should I store it?",
        a: "Keep the cap closed and the jar out of direct sunlight. Always use a dry spoon, because water in the jar can spoil oil. Use it by the date printed on the label.",
      },
      home("Why does it cost more than regular coconut oil?"),
      {
        q: "Why is my size or batch out of stock?",
        a: "We press in small batches from our own coconuts, so the quantity is limited and some batches sell out. Check back soon, or write to us to ask when the next batch is due.",
      },
    ],
  },
  {
    id: "purity",
    title: "Purity and lab testing",
    items: [
      home("How do I know Karijeeva is pure?"),
      {
        q: "Who tests your oil?",
        a: `Every batch goes to an independent food testing lab before it is packed. We have tested ${BRAND.batchesTested} batches so far.`,
        link: { to: "/lab-reports", label: "See the lab reports" },
      },
      {
        q: "What does your lab report show?",
        a: `Two things. A quality report that checks the oil against the FSSAI standard for coconut oil (saponification value, iodine value, acid value, unsaponifiable matter, refractometer reading and the argemone oil test); our latest sample met the standard on all six. And a nutrition panel per 100 g, reference ${LAB_REPORT.ref}. You can read every figure on our lab reports page.`,
        link: { to: "/lab-reports", label: "Read the latest report" },
      },
      {
        q: "Can I get the report for the jar I bought?",
        a: "Yes. Write to us with the batch number printed on your jar and we will send you the lab report for that exact batch.",
        link: { to: "/contact", label: "Write to us" },
      },
      {
        q: "How can I check coconut oil purity at home?",
        a: "Try FSSAI's fridge test. Keep a little oil in a glass in the refrigerator (not the freezer) for 30 minutes. Pure coconut oil sets solid, while other liquid oils mixed in stay as a separate layer. It will not catch every adulterant, which is why lab testing matters too.",
        link: { to: "/how-to-check-coconut-oil-purity", label: "The full purity guide" },
      },
      {
        q: "Is Karijeeva blended with any other oil?",
        a: "No. It is only coconut oil, pressed from our own coconuts. Blending cheaper oils into coconut oil is a known problem in India, which is one reason we test every batch.",
      },
    ],
  },
  {
    id: "using-it",
    title: "Using it: cooking, hair, skin and baby",
    items: [
      home("Can I cook everyday Indian food with it?"),
      {
        q: "Can I deep fry in cold pressed coconut oil?",
        a: "It is better suited to tempering, sautéing and shallow frying at medium heat. Published smoke point figures for coconut oil vary and we have not measured ours, so for deep frying another oil in your rotation is usually the better choice. Whatever you fry in, do not reuse the oil.",
        link: { to: "/cooking-with-coconut-oil", label: "Cooking with coconut oil" },
      },
      {
        q: "Can I use the same oil for cooking and for hair?",
        a: "Yes. It is a food grade, single ingredient oil, which is how many Indian homes have always used coconut oil. Keep a small separate jar for hair so wet or oily fingers never go into your cooking oil.",
      },
      {
        q: "How do I use it for hair?",
        a: "Warm a spoon or two, massage it into the scalp and lengths, and leave it for an hour or overnight before washing. Lab studies on hair fibres found coconut oil used before washing reduced protein loss. It is not a treatment for hair fall.",
        link: { to: "/coconut-oil-for-hair", label: "Coconut oil for hair" },
      },
      {
        q: "Can I use it on my skin and face?",
        a: "A few drops on dry elbows, heels, hands and lips after a bath, while the skin is still damp, work well. On the face, start with a small area, because some people find it too heavy. Patch test first if you have sensitive skin.",
        link: { to: "/coconut-oil-for-skin", label: "Coconut oil for skin" },
      },
      home("Is it safe for baby massage?"),
      {
        q: "How do I do oil pulling?",
        a: "Swish a spoon of oil around your mouth for about ten minutes in the morning, then spit it into the bin, not the sink, because it can set in cold pipes. It is a traditional practice with only small studies behind it, so keep brushing, flossing and seeing your dentist.",
        link: { to: "/oil-pulling-with-coconut-oil", label: "Oil pulling guide" },
      },
    ],
  },
  {
    id: "health",
    title: "Health questions",
    items: [
      home("Is coconut oil healthy?"),
      {
        q: "Does coconut oil raise cholesterol?",
        a: "Compared with unsaturated plant oils, yes. A 2020 meta analysis in the journal Circulation found coconut oil raised LDL cholesterol compared with non tropical vegetable oils. If you have been asked to watch your cholesterol, follow your doctor's advice on saturated fat.",
        link: { to: "/cold-pressed-coconut-oil-benefits", label: "What's traditional, what's proven" },
      },
      {
        q: "How much coconut oil should I use in a day?",
        a: "ICMR NIN's 2024 guidelines suggest about 20 to 30 g of visible fat a day for most sedentary adults, and that counts all oil, ghee and butter together. It also advises using a combination of oils rather than a single one.",
      },
      {
        q: "Does coconut oil help with weight loss?",
        a: "There is no good evidence that it does, and we do not claim it. A 2020 meta analysis in the journal Circulation found coconut oil had no significant effect on body weight compared with other vegetable oils.",
      },
      {
        q: "Is coconut oil the same as MCT oil?",
        a: "No. About half the fatty acids in coconut oil are lauric acid (the Codex standard range is 45.1 to 53.2%), while commercial MCT oil is mostly shorter fatty acids. They are different products.",
      },
      {
        q: "Why don't you call it cholesterol free or heart friendly?",
        a: `Plant oils naturally contain no cholesterol, so it is nothing special, and FSSAI does not allow edible oils high in saturated fat to use that claim. Our oil has ${sat} g saturated fat per 100 g. We also never call it good for the heart, because the evidence does not support that.`,
      },
    ],
  },
  {
    id: "orders",
    title: "Orders, delivery and returns",
    items: [
      home("How long does delivery take, and can I return it?"),
      {
        q: "Do you deliver across India?",
        a: "Yes. We deliver across India and dispatch every order within 24 to 48 hours. Delivery is free on orders above ₹998.",
      },
      {
        q: "How can I pay?",
        a: "Payments go through Razorpay. You can pay by card, UPI, netbanking or wallet.",
      },
      {
        q: "How do I track my order?",
        a: "Once your order is dispatched, you can follow it on our track order page, or from your account if you are signed in.",
        link: { to: "/track-order", label: "Track an order" },
      },
      {
        q: "My jar arrived damaged. What do I do?",
        a: "Write to us with your order number. We will replace the jar or refund you in full.",
        link: { to: "/contact", label: "Contact us" },
      },
      {
        q: "What can I return?",
        a: "Unopened jars can be returned within 7 days of delivery. If a jar arrives damaged, we replace it or refund you in full.",
        link: { to: "/returns-policy", label: "Returns policy" },
      },
    ],
  },
  {
    id: "company",
    title: "The company",
    items: [
      {
        q: "Who makes Karijeeva?",
        a: `Karijeeva is a brand of ${BRAND.company}. We grow the coconuts, press them and pack the oil ourselves.`,
        link: { to: "/about", label: "About us" },
      },
      {
        q: "What is your FSSAI licence number?",
        a: `Our FSSAI licence number is ${BRAND.fssai}. You can check it on the FSSAI website.`,
      },
      {
        q: "Why are there no customer reviews yet?",
        a: "Karijeeva is a new brand. We will not show reviews or ratings until real customers have written them.",
      },
    ],
  },
];

export const ALL_FAQS = FAQ_GROUPS.flatMap((g) => g.items);
